import { useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Loader2 } from "lucide-react";

import { Button } from "@dashai/components/ui/button";
import { ChatChart } from "@dashai/components/dashai/ChatChart";
import { MessageHoverToolbar } from "@dashai/components/dashai/MessageHoverToolbar";
import { SuggestionPrompts } from "@dashai/components/dashai/SuggestionPrompts";
import type { PanelFailure, PanelMessage } from "@dashai/hooks/useDashboardPanelChat";

import { PANEL_POPUP_LAYER } from "./layers";

interface PanelMessagesProps {
  dashboardName: string;
  messages: PanelMessage[];
  /** The panel's chat, once the first question has created it. */
  chatId: string | null;
  suggestions: readonly string[];
  isLoading: boolean;
  failure: PanelFailure | null;
  country: string;
  /** True while the server checks whether this view can take a question. */
  checking: boolean;
  /** Why the panel cannot take a question in this view, or null when it can. */
  blockedReason: string | null;
  onAsk: (question: string) => void;
  onRetry: () => void;
}

/**
 * One answer: its text, its chart when it has one, and copy and thumbs once it
 * is finished. Thumbs need the answer's stored id, so an unsaved one offers copy only.
 */
function PanelAnswer({
  message,
  chatId,
  finished,
}: {
  message: PanelMessage;
  chatId: string | null;
  finished: boolean;
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  const stored = Boolean(message.storedId && chatId);

  return (
    <div>
      <div
        ref={contentRef}
        className="dashai-markdown break-words text-sm leading-relaxed text-foreground"
      >
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
      </div>
      {message.chart && (
        <div className="mt-2 h-48 w-full">
          <ChatChart data={message.chart} embedded />
        </div>
      )}
      {finished && (
        // The toolbar hangs just below its anchor, so the margin keeps it clear of what follows.
        <div className="relative mb-8">
          <MessageHoverToolbar
            messageId={message.storedId ?? message.id}
            chatId={chatId ?? ""}
            role="assistant"
            createdAt={message.createdAt}
            messageContentRef={contentRef}
            isVisible
            stored={stored}
            feedbackClassName={PANEL_POPUP_LAYER}
            onToolbarMouseEnter={() => {}}
            onToolbarMouseLeave={() => {}}
          />
        </div>
      )}
    </div>
  );
}

/** The conversation, with starter questions before the first one. */
export function PanelMessages({
  dashboardName,
  messages,
  chatId,
  suggestions,
  isLoading,
  failure,
  country,
  checking,
  blockedReason,
  onAsk,
  onRetry,
}: PanelMessagesProps) {
  const endRef = useRef<HTMLDivElement>(null);
  const lastContent = messages[messages.length - 1]?.content;

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: "end" });
  }, [messages.length, lastContent, isLoading, failure]);

  const last = messages[messages.length - 1];
  const awaitingAnswer = isLoading && last?.role !== "assistant";
  // Follow-ups belong to the latest answer, and only once it is finished.
  const followUps = !isLoading && last?.role === "assistant" ? (last.suggestions ?? []) : [];

  return (
    <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4" aria-live="polite">
      {blockedReason && (
        <p className="rounded-lg bg-muted p-3 text-sm text-foreground">{blockedReason}</p>
      )}

      {!checking && !blockedReason && messages.length === 0 && !isLoading && !failure && (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Ask anything about <span className="font-medium text-foreground">{dashboardName}</span>.
            Answers use the data and filters on this page.
          </p>
          <div className="flex flex-col gap-2">
            {suggestions.map((s) => (
              <Button
                key={s}
                type="button"
                variant="outline"
                className="h-auto justify-start whitespace-normal rounded-xl py-2 text-left text-sm font-normal"
                onClick={() => onAsk(s)}
              >
                {s}
              </Button>
            ))}
          </div>
        </div>
      )}

      {messages.map((m) =>
        m.role === "user" ? (
          <div key={m.id} className="flex justify-end">
            <div className="max-w-[85%] rounded-2xl bg-primary px-3.5 py-2 text-sm text-primary-foreground">
              {m.content}
            </div>
          </div>
        ) : (
          <PanelAnswer
            key={m.id}
            message={m}
            chatId={chatId}
            finished={!(isLoading && m === last)}
          />
        )
      )}

      {followUps.length > 0 && <SuggestionPrompts suggestions={followUps} onSelect={onAsk} />}

      {awaitingAnswer && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Thinking…
        </div>
      )}

      {failure === "denied" && !blockedReason && (
        <div role="alert" className="rounded-lg bg-amber-500/10 p-3 text-sm text-foreground">
          You don't have access to {country} on this dashboard, so I can't answer about it.
        </div>
      )}

      {failure === "failed" && !blockedReason && (
        <div
          role="alert"
          className="flex items-center justify-between gap-3 rounded-lg bg-muted p-3 text-sm"
        >
          <span>That didn't go through. Try again.</span>
          <Button type="button" size="sm" variant="outline" onClick={onRetry}>
            Retry
          </Button>
        </div>
      )}

      <div ref={endRef} />
    </div>
  );
}
